import React, { useState, useEffect, useRef, useCallback } from 'react';

export function TypingAnimation({
  segments = [], // Array of { text, class }
  duration = 100,
  blinkCursor = true,
  onComplete,
}) {
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const elementRef = useRef(null);
  const typingIndexRef = useRef(0);

  const startTyping = useCallback(() => {
    if (segments.length === 0) return;

    setCurrentSegmentIndex(0);
    setDisplayedText('');
    setIsComplete(false);
    typingIndexRef.current = 0;

    const typeChar = (segIdx, charIdx) => {
      const segment = segments[segIdx];
      if (!segment) {
        setIsComplete(true);
        if (onComplete) onComplete();
        return;
      }

      if (charIdx < segment.text.length) {
        setDisplayedText(segment.text.slice(0, charIdx + 1));
        typingIndexRef.current = charIdx + 1;
        setTimeout(() => typeChar(segIdx, charIdx + 1), duration);
      } else {
        // Segment complete
        if (segIdx < segments.length - 1) {
          // Move to next segment
          setDisplayedText(''); // This might be tricky if we want to keep previous text
          // Actually, we should probably accumulate the text
          setCurrentSegmentIndex(segIdx + 1);
          typingIndexRef.current = 0;
          setTimeout(() => typeChar(segIdx + 1, 0), duration);
        } else {
          setIsComplete(true);
          if (onComplete) onComplete();
        }
      }
    };

    setTimeout(() => typeChar(0, 0), 300);
  }, [segments, duration, onComplete]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startTyping();
        }
      },
      { threshold: 0.5 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [startTyping]);

  return (
    <span ref={elementRef} className="typing-animation-container">
      {segments.map((seg, idx) => (
        <span key={idx} className={seg.class}>
          {idx < currentSegmentIndex ? seg.text : idx === currentSegmentIndex ? displayedText : ''}
        </span>
      ))}
      {blinkCursor && (
        <span className="typing-cursor blink-cursor">|</span>
      )}
    </span>
  );
}
